import React, { RefObject, useEffect, useState } from 'react';
import { ClientOffering } from '../../../services/client-offering';
import MobileHeader from '../MobileHeader';
import MobileProducts from '../MobileProducts';
import MobileTerms from '../MobileTerms';
import MobileBenefits from '../MobileBenefits';
import MobileFooter from '../MobileFooter';
import { useTranslation } from '@wix/wix-i18n-config';
import MobileFeaturesCarousel from '../MobileFeaturesCarousel';
import MobileSubscriptionSelection from '../MobileSubscriptionSelection';
import { PRODUCT } from '../mobile-constant';
import {
  getQueryParam,
  isLocalCyrillic,
  mobileSendMessageToApp,
  navigateToUrl,
  removeDrilldownParams,
  scrollToRef,
} from '../../../services/window-utils';
import {
  getBackPageUrl,
  getPurchasePackageURL,
  getSuccessPageURL,
} from '../../../services/urls';
import { biLogger } from '../../../globals';
import MobileWizard from '../MobileWizard/MobileWizard';

export interface MobileAppProps {
  clientOffering: ClientOffering;
  metaSiteId: string;
}

export const MobileApp: React.FunctionComponent<MobileAppProps> = (
  props: MobileAppProps,
) => {
  const { t } = useTranslation();
  const { clientOffering } = props;
  const scrollRef: RefObject<HTMLDivElement> = React.createRef();

  const [selectedProduct, setSelectedProduct] = useState<number>(1);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(1);
  const scrollNeededToSendBi = 2000;
  const defaultPackageId = 'bcbc441f-47da-4735-b58f-e5386647bf5f';

  useEffect(() => {
    const isCurrentProduct = props.clientOffering.isCurrentProduct;
    if (isCurrentProduct.pro() || isCurrentProduct.unlimited()) {
      setSelectedProduct(PRODUCT.UNLIMITED);
    }

    const productIdParam = getQueryParam().get('productId');
    const cycleIdParam = getQueryParam().get('cycleId');
    if (productIdParam && cycleIdParam) {
      setSelectedProduct(parseInt(productIdParam, 10));
      setSelectedPeriod(parseInt(cycleIdParam, 10));
      setIsSubscriptionSelectionOpen(true);
      removeDrilldownParams();
    }

    mobileSendMessageToApp.init();
    mobileSendMessageToApp.startPage(1);

    const handleScrollBi = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > scrollNeededToSendBi) {
        biLogger.fistTimeScrollToEndTable();
        window.removeEventListener('scroll', handleScrollBi);
      }
    };

    biLogger.init(true);
    biLogger.ppOpen();

    window.addEventListener('scroll', handleScrollBi, { passive: true });
  }, [props.clientOffering.isCurrentProduct]);

  const [
    isSubscriptionSelectionOpen,
    setIsSubscriptionSelectionOpen,
  ] = useState<boolean>(false);

  const handleSelectedProduct = (productIndex: number, buttonType: string) => {
    setSelectedPeriod(1);
    const products = props.clientOffering.getProductList(
      t('package.free.name'),
    );
    const package_name = products[productIndex].name;
    const packageId = products[productIndex].productId || '';
    const isChecked = packageId === defaultPackageId;
    biLogger.ppSelected({ buttonType, package_name, packageId, isChecked });
    if (buttonType === 'hover') {
      scrollToRef(scrollRef);
    }
    return setSelectedProduct(productIndex);
  };

  const handleSelectedPeriod = (periodIndex: number) => {
    return setSelectedPeriod(periodIndex);
  };

  const handleNavigateToPurchasedProduct = () => {
    const msid = props.metaSiteId;
    const { productId, name } = props.clientOffering.getProducts()[
      selectedProduct
    ];
    const cycles = props.clientOffering.products[selectedProduct].cycles;
    const unit = cycles[2 - selectedPeriod].unit;
    const count = cycles[2 - selectedPeriod].count;
    const isChecked = productId === defaultPackageId;

    const currentSubscription =
      props.clientOffering.currentProduct &&
      !props.clientOffering.currentProduct.isBundled
        ? `&existingSubscriptionId=${props.clientOffering.currentProduct.subscriptionId}`
        : '';
    const successPageUrl = encodeURIComponent(
      getSuccessPageURL(msid, productId, unit, count),
    );
    const backPageUrl = encodeURIComponent(
      getBackPageUrl(selectedProduct, selectedPeriod),
    );

    biLogger.cycleSelected({
      cycleName: unit,
      cycleId: `${count}`,
      isChecked,
      packageId: productId,
      package_name: name,
    });

    navigateToUrl(
      `${getPurchasePackageURL()}?siteId=${msid}&productId=${productId}&quantity=1&cycleIntervalUnit=${unit}&cycleIntervalCount=${count}${currentSubscription}&successPageUrl=${successPageUrl}&backPageUrl=${backPageUrl}`,
    );
  };
  const { default: s } = isLocalCyrillic()
    ? require('./MobileAppCyrillic.scss')
    : require('./MobileApp.scss');

  return (
    <div>
      <MobileWizard
        circlesNum={3}
        currentCircle={isSubscriptionSelectionOpen ? 2 : 1}
      />
      {isSubscriptionSelectionOpen ? (
        <div>
          <div className={s.root}>
            <MobileSubscriptionSelection
              clientOffering={clientOffering}
              selectedProduct={selectedProduct}
              selectedPeriod={selectedPeriod}
              setSelectedPeriod={handleSelectedPeriod}
              onClickOnBack={() => {
                mobileSendMessageToApp.startPage(1);
                return setIsSubscriptionSelectionOpen(false);
              }}
            />
            <MobileTerms />
          </div>
          <MobileFooter
            clientOffering={clientOffering}
            selectedProduct={selectedProduct}
            onClickOnUpgrade={handleNavigateToPurchasedProduct}
          />
        </div>
      ) : (
        <div>
          <MobileProducts
            clientOffering={clientOffering}
            selectedProduct={selectedProduct}
            setSelectedProduct={handleSelectedProduct}
            isPinToTop={true}
          />
          <div className={s.root}>
            <MobileHeader />
            <MobileProducts
              clientOffering={clientOffering}
              selectedProduct={selectedProduct}
              setSelectedProduct={handleSelectedProduct}
            />
            <div ref={scrollRef} />
            <MobileFeaturesCarousel
              selectedProductIndex={selectedProduct}
              setSelectedProduct={handleSelectedProduct}
            />
            <MobileBenefits />
            <MobileTerms />
          </div>
          <MobileFooter
            clientOffering={clientOffering}
            selectedProduct={selectedProduct}
            onClickOnUpgrade={() => setIsSubscriptionSelectionOpen(true)}
          />
        </div>
      )}
    </div>
  );
};

export default MobileApp;
